import { UnclearableAutocomplete } from '@src/common/components/Autocomplete';
import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import { useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type FormDropDownOptions = readonly { label: string; value: string }[];
interface Props<T extends FieldValues> {
  fieldName: Path<T>;
  options: FormDropDownOptions;
  control: Control<T>;
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
  value?: string;
  onChange?: (value?: string) => void;
  tabIndex?: number;
}

export const FormDropdown = <T extends FieldValues>({
  fieldName,
  options,
  control,
  label,
  disabled,
  ariaLabel,
  onChange,
  tabIndex,
}: Props<T>) => {
  const fieldId = useId();
  const { t } = useTranslation();

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    onFieldChange: (value: string) => void,
    isDisabled: boolean,
    isFormDisabled: boolean,
  ) => {
    if (event.key === 'Backspace' && !isDisabled && !isFormDisabled) {
      event.preventDefault();
      onFieldChange('');
      onChange?.('');
    }
  };

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({
        field: { disabled: _disabled, ...field },
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => (
        <UnclearableAutocomplete
          disablePortal
          clearOnEscape
          {...field}
          readOnly={disabled || formDisabled}
          options={options}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : ((option as { label: string; value: string }).label ?? '')
          }
          className={fullWidth}
          value={
            field.value
              ? options.filter((option) => field.value === option.value)[0]
              : ''
          }
          onChange={(_, data) => {
            const newValue = (data as { label: string; value: string }).value;
            field.onChange(newValue ?? '');
            onChange?.(newValue);
          }}
          id={fieldId}
          renderInput={(params) => (
            <TextInput
              {...params}
              readOnly={disabled || formDisabled}
              label={label ?? t(`fields.${fieldName}`)}
              error={error && t(`formErrors.${error.message}`)}
              inputProps={{
                ...params.inputProps,
                readOnly: disabled || formDisabled,
                'aria-label': ariaLabel,
                tabIndex,
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) =>
                  handleKeyDown(
                    event,
                    field.onChange,
                    disabled ?? false,
                    formDisabled ?? false,
                  ),
              }}
            />
          )}
          noOptionsText={t('noOptions')}
        />
      )}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
